import { useTranslation } from "react-i18next";
import type { Employee, PertEstimate, SkillLevel } from "../../types";
import { Copy } from "lucide-react";
import { EmployeeEstimateList } from "./EmployeeEstimateList";

const LAST_ESTIMATES_KEY = "roadmap_2026_last_estimates";

interface EstimatesEditorProps {
  employees: Employee[];
  estimates: Record<string, PertEstimate>;
  assigneeIds: string[];
  requiredSkills: Record<string, SkillLevel>;
  onAdd: (employeeId: string) => void;
  onUpdate: (employeeId: string, estimate: PertEstimate) => void;
  onRemove: (employeeId: string) => void;
  onToggleAssignee: (employeeId: string) => void;
}

export function saveLastEstimates(estimates: Record<string, PertEstimate>): void {
  localStorage.setItem(LAST_ESTIMATES_KEY, JSON.stringify({ estimates, timestamp: Date.now() }));
}

export function loadLastEstimates(): Record<string, PertEstimate> | null {
  try {
    const saved = localStorage.getItem(LAST_ESTIMATES_KEY);
    if (!saved) return null;
    const { estimates } = JSON.parse(saved);
    return estimates;
  } catch { return null; }
}

export function EstimatesEditor({
  employees, estimates, assigneeIds, requiredSkills,
  onAdd, onUpdate, onRemove, onToggleAssignee,
}: EstimatesEditorProps) {
  const { t } = useTranslation();
  const hasLastEstimates = loadLastEstimates() !== null;

  const handleCopyFromLast = () => {
    const lastEstimates = loadLastEstimates();
    if (!lastEstimates) return;
    const validEmployeeIds = new Set(employees.map((e) => e.id));
    Object.entries(lastEstimates).forEach(([employeeId, estimate]) => {
      if (validEmployeeIds.has(employeeId)) { onAdd(employeeId); onUpdate(employeeId, estimate); }
    });
  };

  return (
    <div className="space-y-1.5">
      {hasLastEstimates && Object.keys(estimates).length === 0 && (
        <button type="button" onClick={handleCopyFromLast}
          className="w-full flex items-center justify-center gap-1.5 px-2 py-1 text-[10px] text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors">
          <Copy className="w-3 h-3" />
          {t('estimatesEditor.copyFromLast')}
        </button>
      )}

      <EmployeeEstimateList
        employees={employees} estimates={estimates} assigneeIds={assigneeIds}
        requiredSkills={requiredSkills}
        onAdd={onAdd} onUpdate={onUpdate} onRemove={onRemove} onToggleAssignee={onToggleAssignee}
      />
    </div>
  );
}
